import moment from "moment";
import { FC, ReactNode } from "react";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  Hide,
  IconButton,
  IconButtonSize,
  Show
} from "shared/components";
import { Check, RefreshCCW02 } from "untitledui-js-base";
import { ApplicationStatus, VerificationNames } from "shared/types";
import { RerunRequirement } from "shared/types";
import { useDownloadReportPdf } from "../hooks";
import { getRerunRequirementMessage } from "../utils/getRerunRequirementMessage";
import { ResumeTooltip } from "shared/app/OnHoldResume/ResumeTooltip";

type ReportHeaderProps = {
  title: string;
  name: VerificationNames;
  reportDate?: Date | string;
  reportDateText?: string;
  reportDateClassName?: string;
  successLabel?: boolean;
  secondaryText?: string;
  printButtonTitle?: string;
  RequestCountyWalkinButton?: ReactNode;
  AddNewAddressButton?: ReactNode;
  onRerunReport?: () => void;
  reRunReportButtonText?: string;
  applicationStatus?: ApplicationStatus;
  reRunLoading?: boolean;
  rerunRequirements?: RerunRequirement[];
  applicationId?: number | string;
  applicantId?: number | string;
  hidePrint?: boolean;
  titleTrailing?: ReactNode;
  disabledRerunModalTooltip?: string;
};

export const ReportHeader: FC<ReportHeaderProps> = ({
  title,
  name,
  reportDate,
  reportDateText = "Report date",
  reportDateClassName,
  successLabel,
  secondaryText,
  printButtonTitle,
  AddNewAddressButton,
  RequestCountyWalkinButton,
  onRerunReport,
  reRunLoading = false,
  reRunReportButtonText = "Re-run report",
  rerunRequirements,
  applicationId,
  applicantId,
  applicationStatus,
  hidePrint = false,
  titleTrailing,
  disabledRerunModalTooltip = ""
}) => {
  const { startPolling, isPolling } = useDownloadReportPdf({
    applicationId: String(applicationId || 0),
    applicantId: String(applicantId || 0),
    reportId: name
  });
  const rerunRequirementMessage = getRerunRequirementMessage({
    requirement: rerunRequirements?.[0],
    reportName: title
  });
  const getRerunTooltip = (): ReactNode => {
    if (rerunRequirementMessage) return rerunRequirementMessage;
    if (applicationStatus === ApplicationStatus.OnHold && applicationId)
      return <ResumeTooltip applicationId={applicationId} />;
    return disabledRerunModalTooltip;
  };
  const rerunTooltip = getRerunTooltip();

  return (
    <div className="mb-6 flex justify-between gap-1 flex-col md:flex-row print:flex-col">
      <div className="flex items-center">
        <h1 className="flex items-center gap-4 text-xl font-bold text-content-1000">
          <p>
            {title}{" "}
            {secondaryText ? (
              <span className="text-content-100">{secondaryText}</span>
            ) : null}
          </p>

          {titleTrailing}

          {successLabel ? (
            <Badge
              className="ml-2 items-center"
              variant={BadgeVariant.SUCCESS}
              size={BadgeSize.BASE}
              leftElement={<Check size="12px" className="stroke-success-600" />}
            >
              Passed
            </Badge>
          ) : null}
        </h1>
      </div>
      <div className="flex items-center gap-4">
        {reportDate && moment(reportDate).isValid() ? (
          <p className={reportDateClassName || "text-xs text-content-400"}>
            {`${reportDateText}: ${moment(reportDate).format("MMM DD, YYYY [at] h:mm A")}`}
          </p>
        ) : null}
        <div className="no-print shrink-0 flex gap-2 items-center">
          {RequestCountyWalkinButton}
          <Show if={Boolean(onRerunReport)}>
            {reRunReportButtonText ? (
              <Button
                Icon={RefreshCCW02}
                loading={reRunLoading}
                size={ButtonSize.SM}
                tooltipDelay={250}
                tooltip={rerunTooltip}
                disabled={
                  Boolean(rerunRequirements?.length) ||
                  applicationStatus === ApplicationStatus.OnHold ||
                  Boolean(disabledRerunModalTooltip)
                }
                tooltipPlacement="bottom"
                onClick={onRerunReport}
              >
                {reRunReportButtonText}
              </Button>
            ) : (
              <IconButton
                Icon={RefreshCCW02}
                loading={reRunLoading}
                size={IconButtonSize.SM}
                disabled={
                  Boolean(rerunRequirements?.length) ||
                  applicationStatus === ApplicationStatus.OnHold ||
                  Boolean(disabledRerunModalTooltip)
                }
                onClick={onRerunReport}
              />
            )}
          </Show>
          <Hide if={hidePrint || !applicationId || !applicantId}>
            <Button
              disabled={applicationStatus === ApplicationStatus.OnHold}
              tooltip={
                applicationStatus === ApplicationStatus.OnHold &&
                applicationId ? (
                  <ResumeTooltip applicationId={applicationId} />
                ) : undefined
              }
              className="print:hidden hidden md:flex"
              loading={isPolling}
              onClick={() => {
                startPolling({
                  include_government_id_report: false,
                  include_redacted_report: false
                });
              }}
              tooltipDelay={250}
              size={ButtonSize.SM}
            >
              {printButtonTitle ?? "Print report"}
            </Button>
          </Hide>
          {AddNewAddressButton}
        </div>
      </div>
    </div>
  );
};
